import pluralize from "pluralize";

import type {
  BundleDiscount,
  BundleDiscountStructure,
  BundleDiscountTier,
} from "@intelligems/ig-types/dtos/bundle-discount";

export const bundleDiscountStructure = (
  discount: BundleDiscount | null
): BundleDiscountStructure =>
  discount?.discountTiers.length === 1 ? "fixed" : "tiered";

// The minimum isn't stored separately — it's the lowest tier's threshold.
export const bundleMinimum = (discount: BundleDiscount) =>
  Math.min(...discount.discountTiers.map((tier) => tier.minimumProducts));

// Values carry over by position; a new row copies the one above so it's valid.
export const rebaseTiers = (
  tiers: BundleDiscountTier[],
  {
    minimum,
    maxProducts,
    structure,
  }: {
    minimum: number;
    maxProducts: number;
    structure: BundleDiscountStructure;
  }
): BundleDiscountTier[] => {
  const lowest = Math.min(Math.max(minimum, 1), maxProducts);
  const values = [...tiers]
    .sort((a, b) => a.minimumProducts - b.minimumProducts)
    .map((tier) => tier.value);
  const top = structure === "fixed" ? lowest : maxProducts;
  return Array.from({ length: top - lowest + 1 }, (_, index) => ({
    minimumProducts: lowest + index,
    value: values[index] ?? values.at(-1) ?? 0,
  }));
};

// "Customers save 10% off when they add 2 products and 15% off with 3+." Only
// the top tier gets "+", since tiers don't stack.
export const bundleDiscountSummary = (
  discount: BundleDiscount,
  currencySymbol: string | undefined
): string | null => {
  const tiers = discount.discountTiers
    .filter((tier) => tier.value > 0)
    .sort((a, b) => a.minimumProducts - b.minimumProducts);
  if (discount.discountType === "none" || tiers.length === 0) {
    return null;
  }
  const reward = (value: number) =>
    discount.discountType === "fixed"
      ? `${currencySymbol}${value} off`
      : `${value}% off`;
  const parts = tiers.map((tier, index) => {
    const isTop = index === tiers.length - 1;
    const count = `${tier.minimumProducts}${isTop ? "+" : ""}`;
    const noun = isTop
      ? "products"
      : pluralize("product", tier.minimumProducts);
    return index === 0
      ? `${reward(tier.value)} when they add ${count} ${noun}`
      : `${reward(tier.value)} with ${count}`;
  });
  const sentence =
    parts.length <= 2
      ? parts.join(" and ")
      : `${parts.slice(0, -1).join(", ")}, and ${parts.at(-1)}`;
  return `Customers save ${sentence}.`;
};
